import React, { useEffect, useState } from "react";
import {
  FaFilter,
  FaSearch,
  FaChevronRight,
  FaChevronLeft,
} from "react-icons/fa";
import { BsChevronDown, BsChevronUp } from "react-icons/bs";
import { Link } from "react-router-dom";

import {
  getAgeRange,
  getClinicalSite,
  getLocations,
  getStatus,
} from "../../utils/data";
import Footer from "../../components/footer";
import useAllPatients from "../../hooks/useAllPatients";

import moment from "moment";
import Skeleton, { SkeletonTheme } from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";

const ManagePatients = () => {
  const [clinicalSite, setclinicalSite] = useState(null);
  const [locations, setLocations] = useState(null);
  const [ageRange, setAgeRange] = useState(null);
  const [status, setStatus] = useState(null);
  const [clinicalSiteIcon, setClinicalSiteIcon] = useState(false);
  const [locationsIcon, setLocationsIcon] = useState(false);
  const [ageRangeIcon, setAgeRangeIcon] = useState(false);
  const [statusIcon, setStatusIcon] = useState(false);
  const [content, setContent] = useState([]);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  // Search state
  const [searchTerm, setSearchTerm] = useState("");

  const { data, isLoading, error } = useAllPatients(currentPage, pageSize);

  const clinicalIconRotate = clinicalSiteIcon ? "rotate(180deg)" : "rotate(0)";
  const locationsIconRotate = locationsIcon ? "rotate(180deg)" : "rotate(0)";
  const ageRangeIconRotate = ageRangeIcon ? "rotate(180deg)" : "rotate(0)";
  const statusIconRotate = statusIcon ? "rotate(180deg)" : "rotate(0)";
  useEffect(() => {
    if (!isLoading && data) {
      setContent(data.data.content);
    }
  }, [isLoading, data]);
  const handleClinical = (selected) => {
    setclinicalSite(selected);
  };

  const handleLocations = (selected) => {
    setLocations(selected);
  };

  const handleAge = (selected) => {
    setAgeRange(selected);
  };

  const handleStatus = (selected) => {
    setStatus(selected);
  };

  const handlePatient = (user) => {
    localStorage.setItem("patient", user.patient.pommeId);
    localStorage.setItem("localPatient", JSON.stringify(user.patient));
  };

  // Search handlers
  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1); // Reset to first page when search changes
  };

  const clearSearch = () => {
    setSearchTerm("");
    setCurrentPage(1);
  };

  // Filter data based on search term
  const getFilteredData = () => {
    const baseData = data?.data?.content || [];

    if (!searchTerm.trim()) {
      return baseData;
    }

    const searchLower = searchTerm.toLowerCase().trim();

    const filtered = baseData.filter((item) => {
      // Search in POMMe ID
      const pommeIdMatch = item.patient.pommeId
        ?.toString()
        .toLowerCase()
        .includes(searchLower);

      // Search in patient name
      const patientFullName = `${item.patient.firstName || ""} ${
        item.patient.lastName || ""
      }`.toLowerCase();
      const patientLastFirst = `${item.patient.lastName || ""}, ${
        item.patient.firstName || ""
      }`.toLowerCase();
      const patientMatch =
        patientFullName.includes(searchLower) ||
        patientLastFirst.includes(searchLower);

      // Search in gender
      const genderMatch = item.patient.gender
        ?.toLowerCase()
        .includes(searchLower);

      // Search in age
      const ageMatch = item.patient.age?.toString().includes(searchLower);

      // Search in room status
      const roomMatch = (item.room ? "inpatient" : "outpatient").includes(
        searchLower
      );

      return (
        pommeIdMatch || patientMatch || genderMatch || ageMatch || roomMatch
      );
    });

    // Apply client-side pagination to search results
    const startIndex = (currentPage - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    return filtered.slice(startIndex, endIndex);
  };

  // Get total count of filtered results (before pagination)
  const getTotalFilteredCount = () => {
    const isSearchActive = searchTerm.trim().length > 0;
    if (!isSearchActive) {
      return data?.data?.totalElements || 0;
    }

    const baseData = data?.data?.content || [];
    const searchLower = searchTerm.toLowerCase().trim();

    return baseData.filter((item) => {
      // Same search logic as getFilteredData
      const pommeIdMatch = item.patient.pommeId
        ?.toString()
        .toLowerCase()
        .includes(searchLower);
      const patientFullName = `${item.patient.firstName || ""} ${
        item.patient.lastName || ""
      }`.toLowerCase();
      const patientLastFirst = `${item.patient.lastName || ""}, ${
        item.patient.firstName || ""
      }`.toLowerCase();
      const patientMatch =
        patientFullName.includes(searchLower) ||
        patientLastFirst.includes(searchLower);
      const genderMatch = item.patient.gender
        ?.toLowerCase()
        .includes(searchLower);
      const ageMatch = item.patient.age?.toString().includes(searchLower);
      const roomMatch = (item.room ? "inpatient" : "outpatient").includes(
        searchLower
      );

      return (
        pommeIdMatch || patientMatch || genderMatch || ageMatch || roomMatch
      );
    }).length;
  };

  // Pagination handlers
  const handlePageSizeChange = (newPageSize) => {
    setPageSize(newPageSize);
    setCurrentPage(1); // Reset to first page when page size changes
  };

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  const handlePreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handleNextPage = () => {
    const totalPages = Math.ceil((data?.data?.totalElements || 0) / pageSize);
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  // Calculate pagination info
  const isSearchActive = searchTerm.trim().length > 0;
  const totalElements = getTotalFilteredCount();
  const totalPages = Math.ceil(totalElements / pageSize);
  const hasNextPage = currentPage < totalPages;
  const hasPreviousPage = currentPage > 1;

  return (
    <>
      <div className="container-fluid py-3 px-md-4 px-2 h-100 noScroll">
        <div className="card border-0 h-100">
          <div className="card-body px-md-3 px-2 mb-3 d-flex flex-column h-100">
            <div className="flex-shrink-0">
              <h5 className="font-weight-lighter">Admin: Manage Patients</h5>
              <hr />

              <div className="d-flex justify-content-between align-items-start flex-wrap mobile-stack">
                <div className="mb-2 mb-md-0">
                  <h5 className="font-weight-lighter mt-2">
                    Master Patient List
                    {searchTerm.trim() && (
                      <>
                        <br />
                        <small className="text_primary">
                          Search results:{" "}
                          {(() => {
                            const baseData = data?.data?.content || [];
                            const searchLower = searchTerm.toLowerCase().trim();
                            return baseData.filter((item) => {
                              const pommeIdMatch = item.patient.pommeId
                                ?.toString()
                                .toLowerCase()
                                .includes(searchLower);
                              const patientFullName = `${
                                item.patient.firstName || ""
                              } ${item.patient.lastName || ""}`.toLowerCase();
                              const patientLastFirst = `${
                                item.patient.lastName || ""
                              }, ${item.patient.firstName || ""}`.toLowerCase();
                              const patientMatch =
                                patientFullName.includes(searchLower) ||
                                patientLastFirst.includes(searchLower);
                              const genderMatch = item.patient.gender
                                ?.toLowerCase()
                                .includes(searchLower);
                              const ageMatch = item.patient.age
                                ?.toString()
                                .includes(searchLower);
                              const roomMatch = (
                                item.room ? "inpatient" : "outpatient"
                              ).includes(searchLower);
                              return (
                                pommeIdMatch ||
                                patientMatch ||
                                genderMatch ||
                                ageMatch ||
                                roomMatch
                              );
                            }).length;
                          })()}{" "}
                          of {data?.data?.content?.length || 0} patients
                        </small>
                      </>
                    )}
                  </h5>
                </div>
                <div
                  className="d-flex align-items-center mobile-full-width"
                  style={{ maxWidth: "400px" }}
                >
                  <div className="input-group bg_muted rounded flex-grow-1">
                    <div className="input-group-prepend">
                      <span
                        className="input-group-text border-0 bg-transparent text-muted pr-3"
                        id="basic-addon1"
                      >
                        <FaSearch size={16} color="#c2c2c2" />
                      </span>
                    </div>
                    <input
                      type="text"
                      className="form-control border-none bg_muted f-14 text-black outline-none rounded mr-3"
                      placeholder="Search by POMMe ID, name, gender, age, or room status..."
                      aria-label="Search"
                      aria-describedby="basic-addon1"
                      value={searchTerm}
                      onChange={handleSearchChange}
                    />
                  </div>
                  {/* <button
                  className="btn btn-outline-primary ml-2 ml-md-3 rounded py-1 mobile-hide"
                  data-toggle="modal"
                  data-target="#filterModal"
                >
                  <FaFilter size={16} />
                  &nbsp;&nbsp; <small>Filter</small>
                </button> */}
                </div>
              </div>
            </div>

            {/* Flexible Table Area */}
            <div
              className="flex-grow-1 d-flex flex-column mt-3"
              style={{ minHeight: 0 }}
            >
              <div
                className="flex-grow-1 hide-scrollbar table-responsive-mobile"
                style={{
                  overflowY: "auto",
                  scrollbarWidth: "none", // Firefox
                  msOverflowStyle: "none", // IE and Edge
                }}
              >
                <table className="table table-striped table-bordered mb-0 text-center">
                  <thead
                    className="head_light text-white"
                    style={{ position: "sticky", top: 0, zIndex: 10 }}
                  >
                    <tr>
                      <th scope="col" style={{ minWidth: "100px" }}>
                        POMMe ID
                      </th>
                      <th scope="col" style={{ minWidth: "120px" }}>
                        Last Name
                      </th>
                      <th scope="col" style={{ minWidth: "120px" }}>
                        First Name
                      </th>
                      <th scope="col" style={{ minWidth: "80px" }}>
                        Gender
                      </th>
                      <th scope="col" style={{ minWidth: "60px" }}>
                        Age
                      </th>
                      <th scope="col" style={{ minWidth: "120px" }}>
                        DOB
                      </th>
                      <th scope="col" style={{ minWidth: "140px" }}>
                        Clinical Site / Location
                      </th>
                      <th scope="col" style={{ minWidth: "150px" }}>
                        Go to Patient Home Page
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {(() => {
                      const filteredData = getFilteredData();
                      return !isLoading && filteredData.length > 0 ? (
                        filteredData.map((item, i) => (
                          <tr key={i}>
                            <th>
                              <small>{item?.patient?.pommeId}</small>
                            </th>
                            <td>
                              <small>{item?.patient?.lastName}</small>
                            </td>
                            <td>
                              <small>{item?.patient?.firstName}</small>
                            </td>
                            <td>
                              <small>{item?.patient?.gender}</small>
                            </td>
                            <td>
                              <small>{item?.patient?.age}</small>
                            </td>
                            <td>
                              <small>
                                {moment
                                  .utc(item?.patient?.dob)
                                  .local()
                                  .format("D MMM YYYY")}
                              </small>
                            </td>
                            <td>
                              <small>
                                {
                                  item?.clinicalLocationWithSite[0]
                                    ?.clinicalSite?.name
                                }

                                {`/ ${
                                  item?.clinicalLocationWithSite[0]
                                    ?.clinicalLocation?.name
                                    ? item?.clinicalLocationWithSite[0]
                                        ?.clinicalLocation?.name
                                    : ""
                                }`}
                              </small>
                            </td>
                            <td>
                              <Link
                                to={`/admin/patient/details/${item.patient.pommeId}`}
                                onClick={() => handlePatient(item)}
                                className="border_success rounded px-4"
                              >
                                <i className="fa fa-chevron-right" />
                              </Link>
                            </td>
                          </tr>
                        ))
                      ) : !isLoading ? (
                        <tr>
                          <td colSpan="8" className="text-center py-4">
                            <small className="text-muted">
                              {searchTerm.trim()
                                ? `No patients found matching "${searchTerm}"`
                                : "No patients found"}
                            </small>
                          </td>
                        </tr>
                      ) : null;
                    })()}
                    {isLoading && (
                      <SkeletonTheme
                        baseColor="#e9ecef"
                        highlightColor="#f8f9fa"
                      >
                        {[...Array(8)].map((_, index) => (
                          <tr key={`skeleton-${index}`}>
                            <th>
                              <small>
                                <Skeleton width={80} height={12} />
                              </small>
                            </th>
                            <td>
                              <small>
                                <Skeleton width={100} height={12} />
                              </small>
                            </td>
                            <td>
                              <small>
                                <Skeleton width={90} height={12} />
                              </small>
                            </td>
                            <td>
                              <small>
                                <Skeleton width={60} height={12} />
                              </small>
                            </td>
                            <td>
                              <small>
                                <Skeleton width={40} height={12} />
                              </small>
                            </td>
                            <td>
                              <small>
                                <Skeleton width={100} height={12} />
                              </small>
                            </td>
                            <td>
                              <small>
                                <Skeleton width={80} height={12} />
                              </small>
                            </td>
                            <td>
                              <Skeleton width={40} height={16} />
                            </td>
                          </tr>
                        ))}
                      </SkeletonTheme>
                    )}
                    {!isLoading &&
                      (!data?.data || data.data.content.length === 0) && (
                        <tr>
                          <td colSpan="8" className="text-center py-4">
                            <small className="text-muted">
                              No patients found
                            </small>
                          </td>
                        </tr>
                      )}
                  </tbody>
                </table>
              </div>

              {/* Fixed Pagination Controls - Bottom of card */}
              {!isLoading && data?.data && data.data.content.length > 0 && (
                <div className="flex-shrink-0 d-flex justify-content-between align-items-center mt-4 flex-wrap mobile-stack">
                  {/* Page Size Dropdown - Bottom Left */}
                  <div className="d-flex align-items-center mb-2 mb-md-0 mobile-text-center">
                    <span
                      className="text-dark mr-2"
                      style={{ fontSize: "12px", fontWeight: "500" }}
                    >
                      Show
                    </span>
                    <select
                      className="border rounded"
                      style={{
                        width: "50px",
                        height: "24px",
                        fontSize: "12px",
                        fontWeight: "500",
                        color: "#495057",
                        backgroundColor: "#fff",
                        border: "1px solid #ced4da",
                        borderRadius: "6px",
                        padding: "2px 4px",
                        outline: "none",
                      }}
                      value={pageSize}
                      onChange={(e) =>
                        handlePageSizeChange(parseInt(e.target.value))
                      }
                    >
                      <option value={5}>5</option>
                      <option value={10}>10</option>
                      <option value={15}>15</option>
                      <option value={20}>20</option>
                      <option value={25}>25</option>
                    </select>
                    <span
                      className="text-dark ml-2"
                      style={{ fontSize: "12px", fontWeight: "500" }}
                    >
                      entries
                    </span>
                  </div>

                  {/* Pagination Navigation - Bottom Right */}
                  <div className="d-flex align-items-center flex-column flex-md-row">
                    <span
                      className="text-muted mr-0 mr-md-4 mb-2 mb-md-0"
                      style={{ fontSize: "12px" }}
                    >
                      {totalElements > 0
                        ? `Showing ${Math.min(
                            (currentPage - 1) * pageSize + 1,
                            totalElements
                          )} to ${Math.min(
                            currentPage * pageSize,
                            totalElements
                          )} of ${totalElements} entries`
                        : "No entries to show"}
                    </span>

                    {totalPages > 1 && (
                      <nav aria-label="Page navigation">
                        <div className="d-flex align-items-center pagination-mobile">
                          {/* Previous Button */}
                          <button
                            className="btn d-flex align-items-center justify-content-center mr-2"
                            style={{
                              width: "24px",
                              height: "24px",
                              border: "1px solid #ced4da",
                              borderRadius: "6px",
                              backgroundColor: !hasPreviousPage
                                ? "#d7d7d7"
                                : "#fff",
                              color: !hasPreviousPage
                                ? "rgba(83, 89, 107, 0.5)"
                                : "#495057",
                              cursor: !hasPreviousPage
                                ? "not-allowed"
                                : "pointer",
                              paddingLeft: "7px",
                              paddingRight: "7px",
                            }}
                            onClick={handlePreviousPage}
                            disabled={!hasPreviousPage}
                          >
                            <FaChevronLeft size={12} />
                          </button>

                          {/* Page Numbers - Show fewer on mobile */}
                          <div className="d-flex">
                            {(() => {
                              const pages = Array.from(
                                { length: totalPages },
                                (_, i) => i + 1
                              );

                              // On mobile, show max 5 pages
                              if (window.innerWidth <= 768 && totalPages > 5) {
                                const start = Math.max(1, currentPage - 2);
                                const end = Math.min(totalPages, start + 4);
                                return pages.slice(start - 1, end);
                              }

                              return pages;
                            })().map((pageNum) => (
                              <button
                                key={pageNum}
                                className="btn d-flex align-items-center justify-content-center mx-1"
                                style={{
                                  width: "24px",
                                  height: "24px",
                                  border:
                                    currentPage === pageNum
                                      ? "1px solid #53596b"
                                      : "1px solid #ced4da",
                                  borderRadius: "6px",
                                  backgroundColor:
                                    currentPage === pageNum
                                      ? "#53596b"
                                      : "#fff",
                                  color:
                                    currentPage === pageNum
                                      ? "#fff"
                                      : "#495057",
                                  fontSize: "12px",
                                  fontWeight: "500",
                                  cursor: "pointer",
                                }}
                                onClick={() => handlePageChange(pageNum)}
                              >
                                {pageNum}
                              </button>
                            ))}
                          </div>

                          {/* Next Button */}
                          <button
                            className="btn d-flex align-items-center justify-content-center ml-2"
                            style={{
                              width: "24px",
                              height: "24px",
                              border: "1px solid #ced4da",
                              borderRadius: "6px",
                              backgroundColor: !hasNextPage
                                ? "#d7d7d7"
                                : "#fff",
                              color: !hasNextPage
                                ? "rgba(83, 89, 107, 0.5)"
                                : "#495057",
                              cursor: !hasNextPage ? "not-allowed" : "pointer",
                              paddingLeft: "7px",
                              paddingRight: "7px",
                            }}
                            onClick={handleNextPage}
                            disabled={!hasNextPage}
                          >
                            <FaChevronRight size={12} />
                          </button>
                        </div>
                      </nav>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      <div
        className="modal fade"
        id="filterModal"
        tabIndex="-1"
        aria-labelledby="exampleModalLabel"
        aria-hidden="true"
      >
        <div
          className="modal-dialog modal-dialog-centered"
          style={{ maxWidth: "95%", width: "395px" }}
        >
          <div
            className="modal-content pt-3 border-0 mobile-compact"
            style={{ borderRadius: "10px" }}
          >
            <div className="modal-header border-0">
              <div className="flex-grow-1 text-center">
                <h5 className="text_primary" style={{ fontWeight: 700 }}>
                  Search Filter
                </h5>
              </div>
              <button
                type="button"
                className="close"
                data-dismiss="modal"
                aria-label="Close"
              >
                <span
                  className="text_primary"
                  style={{ fontSize: 33, fontWeight: "600" }}
                  aria-hidden="true"
                >
                  &times;
                </span>
              </button>
            </div>
            <div className="modal-body px-3">
              <div className="dropdown mb-4">
                <div className="dropdown">
                  <button
                    className="btn btn-block border text-left overlay-dropdown-input"
                    type="button"
                    id="dropdownMenuButton"
                    data-toggle="dropdown"
                    data-display="static"
                    aria-haspopup="true"
                    aria-expanded="false"
                    onClickCapture={() =>
                      setClinicalSiteIcon(!clinicalSiteIcon)
                    }
                  >
                    <div className="d-flex justify-content-between">
                      <small>Choose Clinical Site / Location</small>
                      <BsChevronDown
                        style={{
                          transform: clinicalIconRotate,
                          transition: "all 0.3s linear",
                        }}
                      />
                    </div>
                  </button>
                  <div
                    className="dropdown-menu dropdown-menu--animate custom border-0 shadow overlay-dropdown-container"
                    aria-labelledby="dropdownMenuButton"
                  >
                    <p className="pl-2 mb-0 pb-2">
                      <small>Choose as many as applicable:</small>
                    </p>
                    {getClinicalSite.map((item, key) => (
                      <div
                        className="dropdown-item noFilterHover form-group form-check ml-4 wa nohover"
                        key={key}
                      >
                        <input
                          type="checkbox"
                          className="form-check-input mt-2"
                          id="exampleCheck1"
                          onChange={() => handleClinical(item.value)}
                        />
                        <label
                          className="form-check-label"
                          htmlFor="exampleCheck1"
                        >
                          <small>{item.label}</small>
                        </label>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
              <div className="dropdown mb-4">
                <div className="dropdown">
                  <button
                    className="btn btn-block border text-left overlay-dropdown-input"
                    type="button"
                    id="dropdownMenuButton"
                    data-toggle="dropdown"
                    data-display="static"
                    aria-haspopup="true"
                    aria-expanded="false"
                    onClickCapture={() => setLocationsIcon(!locationsIcon)}
                  >
                    <div className="d-flex justify-content-between">
                      <small>Choose Location</small>
                      <BsChevronDown
                        style={{
                          transform: locationsIconRotate,
                          transition: "all 0.3s linear",
                        }}
                      />
                    </div>
                  </button>
                  <div
                    className="dropdown-menu dropdown-menu--animate custom border-0 shadow overlay-dropdown-container"
                    aria-labelledby="dropdownMenuButton"
                  >
                    <p className="pl-2 mb-0 pb-2">
                      <small>Choose as many as applicable:</small>
                    </p>
                    {getLocations.map((item, key) => (
                      <div
                        className="dropdown-item noFilterHover form-group form-check ml-4 wa nohover"
                        key={key}
                      >
                        <input
                          type="checkbox"
                          className="form-check-input mt-2"
                          id="exampleCheck1"
                          onChange={() => handleLocations(item.value)}
                        />
                        <label
                          className="form-check-label"
                          htmlFor="exampleCheck1"
                        >
                          <small>{item.label}</small>
                        </label>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
              <div className="dropdown mb-4">
                <div className="dropdown">
                  <button
                    className="btn btn-block border text-left overlay-dropdown-input"
                    type="button"
                    id="dropdownMenuButton"
                    data-toggle="dropdown"
                    data-display="static"
                    aria-haspopup="true"
                    aria-expanded="false"
                    onClickCapture={() => setAgeRangeIcon(!ageRangeIcon)}
                  >
                    <div className="d-flex justify-content-between">
                      <small>Choose Age Range</small>
                      <BsChevronDown
                        style={{
                          transform: ageRangeIconRotate,
                          transition: "all 0.3s linear",
                        }}
                      />
                    </div>
                  </button>
                  <div
                    className="dropdown-menu dropdown-menu--animate custom border-0 shadow overlay-dropdown-container"
                    aria-labelledby="dropdownMenuButton"
                  >
                    <p className="pl-2 mb-0 pb-2">
                      <small>Choose as many as applicable:</small>
                    </p>
                    {getAgeRange.map((item, key) => (
                      <div
                        className="dropdown-item noFilterHover form-group form-check ml-4 wa nohover"
                        key={key}
                      >
                        <input
                          type="checkbox"
                          className="form-check-input mt-2"
                          id="exampleCheck1"
                          onChange={() => handleAge(item.value)}
                        />
                        <label
                          className="form-check-label"
                          htmlFor="exampleCheck1"
                        >
                          <small>{item.label}</small>
                        </label>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
              <div className="dropdown mb-4">
                <div className="dropdown">
                  <button
                    className="btn btn-block border text-left overlay-dropdown-input"
                    type="button"
                    id="dropdownMenuButton"
                    data-toggle="dropdown"
                    data-display="static"
                    aria-haspopup="true"
                    aria-expanded="false"
                    onClickCapture={() => setStatusIcon(!statusIcon)}
                  >
                    <div className="d-flex justify-content-between">
                      <small>Choose Status</small>
                      <BsChevronDown
                        style={{
                          transform: statusIconRotate,
                          transition: "all 0.3s linear",
                        }}
                      />
                    </div>
                  </button>
                  <div
                    className="dropdown-menu dropdown-menu--animate custom border-0 shadow overlay-dropdown-container"
                    aria-labelledby="dropdownMenuButton"
                  >
                    <p className="pl-4 mb-0 pb-2">
                      <small>Choose one status only:</small>
                    </p>
                    {getStatus.map((item, key) => (
                      <a
                        onClick={() => handleStatus(item.value)}
                        className="dropdown-item activeHover ml-4 wa"
                        key={key}
                        href="#"
                      >
                        <small>{item.label}</small>
                      </a>
                    ))}
                  </div>
                </div>
              </div>
            </div>
            <div className="modal-footer d-flex justify-content-between align-items-center border-0">
              <button
                type="button"
                className="btn btn_outline_danger overlay-btn"
                data-dismiss="modal"
              >
                CLEAR
              </button>
              <button
                type="button"
                className="btn btn-outline-primary overlay-btn"
              >
                SEARCH
              </button>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </>
  );
};

export default ManagePatients;
